import { useEffect, useState } from "react";
import { ChevronDown, ChevronRight, ShieldAlert } from "lucide-react";
import api from "@/api";
import { useI18n } from "@/i18n";

// READ-ONLY watchtower, not a queue: a 34-year-old reached the public watchlist on 20/09
// because a provider "0" was taken as proof. These three indicators make that visible
// BEFORE a visitor sees it. No action button on purpose.
const KEYS = ["implausible_debut", "inconsistent_first_season", "zero_apps_over_21", "fd_merge_doubts"];

export default function AnomaliesPanel() {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/anomalies").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const clean = KEYS.every((k) => (data.public_counts[k] || 0) === 0);

  return (
    <div className="mb-6 rounded-xl p-5 hud-card" data-testid="anomalies-panel">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-widest text-slate-500">
          <ShieldAlert className={`h-3.5 w-3.5 ${clean ? "text-[#00FF87]" : "text-[#FFD600]"}`} />
          {t("admin.anomalies_title")}
        </span>
        {KEYS.map((k) => (
          <button
            key={k}
            onClick={() => setOpen(open === k ? null : k)}
            data-testid={`anomaly-toggle-${k}`}
            className="flex items-center gap-1 font-mono2 text-xs text-slate-300 underline-offset-4 hover:underline"
          >
            {open === k ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            <b className={`font-display text-2xl ${data.public_counts[k] ? "text-[#FFD600]" : "text-[#00FF87]"}`}>
              {data.public_counts[k]}
            </b>
            <span className="text-slate-400">{t(`admin.anomaly_${k}`)}</span>
            <span className="text-slate-600">({data.counts[k]})</span>
          </button>
        ))}
      </div>
      <p className="mt-2 font-mono2 text-[10px] text-slate-600">{t("admin.anomalies_note")}</p>

      {open && (
        <div className="mt-3 overflow-x-auto" data-testid={`anomaly-rows-${open}`}>
          {data.indicators[open].length === 0 ? (
            <p className="font-mono2 text-[10px] text-slate-500">{t("admin.anomalies_empty")}</p>
          ) : (
            <table className="w-full min-w-[620px] font-mono2 text-[10px]">
              <thead className="text-slate-500">
                <tr className="text-left uppercase tracking-wider">
                  <th className="py-1">{t("admin.anomaly_player")}</th>
                  <th className="py-1 text-right">{t("player.age")}</th>
                  <th className="py-1 text-right">{t("admin.anomaly_first_season")}</th>
                  <th className="py-1 text-right">{t("admin.anomaly_apps")}</th>
                  <th className="py-1 pl-4">{t("admin.anomaly_where")}</th>
                  <th className="py-1 pl-4">{t("admin.anomaly_status")}</th>
                </tr>
              </thead>
              <tbody className="text-slate-300">
                {data.indicators[open].map((r) => (
                  <tr key={`${open}-${r.player_id}`} className="border-t border-slate-800/60">
                    <td className="py-1">{r.name}</td>
                    <td className="py-1 text-right">{r.age ?? "—"}</td>
                    <td className="py-1 text-right">{r.first_season ?? "—"}</td>
                    <td className="py-1 text-right">{r.apps_basis ?? "—"}</td>
                    <td className="py-1 pl-4 text-slate-400">{[r.club, r.league].filter(Boolean).join(" · ")}</td>
                    <td className={`py-1 pl-4 ${r.public ? "text-[#FFD600]" : "text-slate-500"}`}>
                      {r.status}{r.public ? ` · ${t("admin.anomaly_public")}` : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
